import { FC } from 'react';

import { useTranslate } from 'hooks/useTranslate';
import { HoldingClass, Range } from 'types';

import ToggleMenu from 'modules/_shared/components/ToggleMenu';
import { formatNumber, formatNumberDecimals } from 'utils/formats';

import { useHoldingClassListPage } from '../context/HoldingClassListPageContext';
import { getMarginSeparator } from '../utils/getMarginSeparator';
import RangeRowLine from './RangeRowLine';
import { useGetHoldingClassRowData } from '../hooks/useGetHoldingClassRowData';

type HoldingClassTableRowProps = {
  index: number;
  holdingClass: HoldingClass;
};

const HoldingClassTableRow: FC<HoldingClassTableRowProps> = ({
  index,
  holdingClass,
}) => {
  const { t } = useTranslate();

  const { data, actions } = useHoldingClassListPage();
  const { data: rowData } = useGetHoldingClassRowData({
    holdingClass,
    societyNominalValue: data.societyNominalValue,
  });

  const handleView = () => {
    actions.handleViewClass(index);
  };

  const handleEdit = () => {
    actions.handleEditClass(index);
  };

  const toggleOptions = [
    <a
      className="d-flex cursor-pointer"
      key={`holding-class-view-option-${index}`}
      onClick={handleView}
    >
      <em className="icon ni ni-eye fs-20px" />
      <span className="ml-1">{t('See')}</span>
    </a>,
    <a
      className="d-flex cursor-pointer"
      key={`holding-class-edit-option-${index}`}
      onClick={handleEdit}
    >
      <em className="icon ni ni-edit fs-20px" />
      <span className="ml-1">{t('Edit')}</span>
    </a>,
  ];

  const hasOptions = true;

  return (
    <>
      <td className="nk-tb-col text-center" key={`holding-class-name-${index}`}>
        <span className="text-primary font-weight-bold">{rowData.name}</span>
      </td>

      <td
        className="nk-tb-col text-right"
        key={`holding-class-nominal-value-${index}`}
      >
        <span>{formatNumberDecimals(rowData.nominalValue)}</span>
      </td>

      <td
        className="nk-tb-col text-right"
        key={`holding-class-shares-count-${index}`}
      >
        <span>{formatNumber(rowData.sharesCount)}</span>
      </td>

      <td
        className="nk-tb-col text-right"
        key={`holding-class-numeration-value-${index}`}
      >
        {rowData.ranges?.slice(0, 5).map((range: Range, rangeIndex: number) => (
          <div
            key={`${index}-${range.from}-${range.to}`}
            style={getMarginSeparator(rangeIndex)}
          >
            <RangeRowLine range={range} />
          </div>
        ))}
        {rowData.ranges.length > 5 && (
          <div className="mt-1 ff-italic text-muted fs-12px">
            ...
            {t('AndXNumerationsMore', {
              count: formatNumber(rowData.ranges.length - 5),
            })}
          </div>
        )}
      </td>

      <td className="nk-tb-col text-right" key={`holding-class-votes-${index}`}>
        <span>{rowData.votes}</span>
      </td>

      <td
        className="nk-tb-col tb-col-xl text-center"
        key={`holding-class-numeration-${index}`}
      >
        <span>{rowData.numerationLabel}</span>
      </td>

      <td
        className="nk-tb-col text-left"
        key={`holding-class-liquidation-${index}`}
      >
        <span>{rowData.liquidationLabel}</span>
      </td>

      <td
        className="nk-tb-col text-left"
        key={`holding-class-antidilution-${index}`}
      >
        <span>{rowData.antidilutionLabel} </span>
      </td>
      {/*
      <td
        className="nk-tb-col tb-col-xl text-left"
        key={`holding-class-lockup-period-${index}`}
      >
        <span>{rowData.lockupPeriodLabel}</span>
      </td>

      <td
        className="nk-tb-col tb-col-xl text-left"
        key={`holding-class-conversion-${index}`}
      >
        <span>{rowData.conversionLabel}</span>
      </td>

      <td
        className="nk-tb-col tb-col-xl text-left"
        key={`holding-class-conversion-rate-${index}`}
      >
        <span>{formatNumber(rowData.conversionRate)} </span>
      </td> */}

      {hasOptions && (
        <td className="nk-tb-col" key={`holding-class-options-${index}`}>
          <ToggleMenu toggleOptions={toggleOptions} />
        </td>
      )}
    </>
  );
};

export default HoldingClassTableRow;
